import React, { useState, useEffect } from "react";
import api from "./api";
import useAuth from "../hooks/useAuth";
import "./TherapistDashboard.css";

/* ── Pre-defined issue categories ────────────────────────────────────────── */
const ISSUE_CATEGORIES = [
  'Emergency / Life Threatening',
  'Severe Pain / Discomfort',
  'Medication Concern',
  'Treatment Non-Response',
  'Critical Vitals',
  'Psychological / Behavioral',
  'Equipment Related Injury',
  'Patient Request / Complaint',
  'Other',
];

/* ── Doctor acknowledgment reason categories ─────────────────────────────── */
const ACK_CATEGORIES = [
  'Treated / Resolved On-Site',
  'Monitoring Required',
  'Referred to Specialist',
  'Patient Education Provided',
  'Treatment Protocol Changed',
  'Sent to Emergency',
  'Not Critical — No Action Needed',
  'Other',
];

/* ── Status badge colours ─────────────────────────────────────────────────── */
const statusStyle = (acknowledged, isOverSLA) => {
  if (acknowledged)  return { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' };
  if (isOverSLA)     return { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' };
  return               { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' };
};

const EscalationModal = ({
  appointment: original,
  onClose,
  patientCard,
  patientName: propPatientName,
  refreshSequences,
  variant = 'modal',
}) => {
  const { auth } = useAuth();

  const userRole =
    auth.roles.includes("solution")   ? "solution"   :
    auth.roles.includes("owner")      ? "owner"      :
    auth.roles.includes("superadmin") ? "superadmin" :
    auth.roles.includes("admin")      ? "admin"      :
    auth.roles.includes("incharge")   ? "incharge"   : "therapist";

  /* ── Form state ── */
  const [issueCategory,    setIssueCategory]    = useState('');
  const [comment,          setComment]          = useState('');
  const [selectedDoctor,   setSelectedDoctor]   = useState('');

  /* ── Data ── */
  const [doctors,          setDoctors]          = useState([]);   // owner/superadmin users
  const [escalationNotifs, setEscalationNotifs] = useState([]);   // live notifs for this patient
  const [history,          setHistory]          = useState([]);   // previousEscalations from patient

  /* ── Acknowledge panel state (doctor side) ── */
  const [ackNotifId,       setAckNotifId]       = useState(null);
  const [ackCategory,      setAckCategory]      = useState('');
  const [ackComment,       setAckComment]       = useState('');

  /* ── UI ── */
  const [saving,           setSaving]           = useState(false);
  const [formError,        setFormError]        = useState('');
  const [formSuccess,      setFormSuccess]      = useState('');
  const [loadError,        setLoadError]        = useState('');

  useEffect(() => {
    fetchDoctors();
    fetchHistory();
    fetchEscalationNotifs();
  }, []); // eslint-disable-line

  const fetchDoctors = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/users/all`);
      const docs = (res.data || []).filter(u =>
        Array.isArray(u.roles)
          ? u.roles.some(r => ['owner', 'superadmin'].includes(r))
          : ['owner', 'superadmin'].includes(String(u.roles))
      );
      setDoctors(docs);
    } catch (e) { /* non-fatal */ }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/patients/profiles/${patientCard}`);
      setHistory(res.data?.previousEscalations || []);
    } catch (e) {
      setLoadError("Could not load escalation history.");
    }
  };

  const fetchEscalationNotifs = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/notifications/escalations/${patientCard}`);
      setEscalationNotifs(res.data || []);
    } catch (e) { /* non-fatal */ }
  };

  /* ── Derived: escalations awaiting THIS doctor's ack ── */
  const pendingMyAck = escalationNotifs.filter(n =>
    n.userId === auth.username && !n.acknowledged && !n.resolved
  );

  /* ── Submit new escalation ── */
  const handleEscalate = async () => {
    setFormError(''); setFormSuccess('');
    if (!issueCategory)        { setFormError("Select an issue category."); return; }
    if (!comment.trim())       { setFormError("Describe the issue in the comment field."); return; }
    if (!selectedDoctor)       { setFormError("Select the doctor to escalate to."); return; }

    setSaving(true);
    try {
      const patName = propPatientName || original?.name || patientCard;
      const doctorUser = doctors.find(d => d.username === selectedDoctor);

      /* 1 — Save to patient record (existing flow) */
      const newEntry = {
        commentsByPt:        '',
        commentsByThe:       comment.trim(),
        escalatedTo:         selectedDoctor,
        escalatedToName:     doctorUser?.username || selectedDoctor,
        escalationDate:      new Date(),
        commentsByDr:        '',
        commentsByAdmin:     '',
        commentsByIncharge:  '',
        commentsByTherapist: '',
        issueCategory,
      };
      await api.post(
        `${process.env.REACT_APP_URL}/patients/consultation/patientsequences/escalate/${patientCard}`,
        { previousEscalations: [...history, newEntry], escalatedTo: userRole }
      );

      /* 2 — Create notification + OTT task + calendar appointment */
      await api.post(`${process.env.REACT_APP_URL}/notifications/escalate`, {
        doctorUsername:      selectedDoctor,
        issueCategory,
        comment:             comment.trim(),
        patientCard,
        patientName:         patName,
        appointmentId:       original?._id || '',
        escalatedByUsername: auth?.username,
        escalatorName:       auth?.username,
      });

      setFormSuccess(`✅ Escalation sent to ${selectedDoctor}. They will be reminded every 3 hours until acknowledged.`);
      setIssueCategory(''); setComment(''); setSelectedDoctor('');
      refreshSequences?.();
      onClose?.(true); // signal success to parent so it can mark appointment as escalated
      await fetchHistory();
      await fetchEscalationNotifs();
    } catch (e) {
      setFormError("Escalation failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Doctor acknowledges escalation ── */
  const handleAcknowledge = async (notifId) => {
    setFormError(''); setFormSuccess('');
    if (!ackCategory)       { setFormError("Select a reason category."); return; }
    if (!ackComment.trim()) { setFormError("Enter acknowledgment notes."); return; }

    setSaving(true);
    try {
      await api.patch(`${process.env.REACT_APP_URL}/notifications/${notifId}/acknowledge-escalation`, {
        reasonCategory:        ackCategory,
        reasonComment:         ackComment.trim(),
        acknowledgedByUsername: auth?.username,
      });
      setFormSuccess("✅ Escalation acknowledged and closed.");
      setAckNotifId(null); setAckCategory(''); setAckComment('');
      await fetchEscalationNotifs();
      await fetchHistory();
    } catch (e) {
      setFormError("Could not acknowledge. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ── History row status ── */
  const getEntryStatus = (entry) => {
    const liveNotif = escalationNotifs.find(n => {
      const diff = Math.abs(new Date(n.createdAt) - new Date(entry.escalationDate));
      return diff < 120000; // within 2 min = same escalation
    });
    if (!liveNotif) return 'No notification';
    const isOverSLA = (Date.now() - new Date(liveNotif.createdAt)) >= 48 * 3600 * 1000;
    return { liveNotif, isOverSLA };
  };

  /* ─────────────────────────────────────────────────────────
     CONTENT
  ───────────────────────────────────────────────────────── */
  const content = (
    <div style={{ padding: variant === 'modal' ? 0 : '4px 0' }}>

      {/* Title */}
      <h3 style={{ marginTop: 0, fontWeight: 700, fontSize: 17, color: '#1a1d23', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        🚨 Escalation
        {pendingMyAck.length > 0 && (
          <span style={{ background: '#ef4444', color: '#fff', borderRadius: 999, fontSize: 11, padding: '2px 8px', fontWeight: 800 }}>
            {pendingMyAck.length} pending your ack
          </span>
        )}
      </h3>

      {loadError   && <div style={msgStyle('#fef2f2','#fca5a5','#b91c1c')}>⚠️ {loadError}</div>}
      {formError   && <div style={msgStyle('#fef2f2','#fca5a5','#b91c1c')}>⚠️ {formError}</div>}
      {formSuccess && <div style={msgStyle('#f0fdf4','#86efac','#166534')}>{formSuccess}</div>}

      {/* ══════════════════════════════════════════════════
          DOCTOR: Acknowledgment panels
      ══════════════════════════════════════════════════ */}
      {pendingMyAck.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#991b1b', marginBottom: 12 }}>
            🔴 Escalation(s) Awaiting Your Acknowledgment
          </div>

          {pendingMyAck.map(n => {
            const age       = Date.now() - new Date(n.createdAt);
            const isOverSLA = age >= 48 * 3600 * 1000;
            const ageH      = Math.round(age / 3600000);
            const isExpanded = ackNotifId === n._id;

            return (
              <div key={n._id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #fecaca', lastChildBorderBottom: 'none' }}>
                {/* Header */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ ...badgeStyle, ...statusStyle(false, isOverSLA) }}>
                    {isOverSLA ? `⚠️ OVERDUE ${ageH}h` : `⏳ ${ageH}h ago`}
                  </span>
                  <span style={{ ...badgeStyle, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>
                    {n.issueCategory || 'Escalation'}
                  </span>
                </div>

                <div style={{ fontSize: 12, color: '#374151', marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                  {n.message}
                </div>

                {/* Acknowledgment form */}
                {isExpanded ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div>
                      <label style={labelStyle}>Reason Category *</label>
                      <select value={ackCategory} onChange={e => setAckCategory(e.target.value)} style={selStyle}>
                        <option value="">— Select reason —</option>
                        {ACK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Your Notes / Action Taken *</label>
                      <textarea rows="3" value={ackComment}
                        onChange={e => setAckComment(e.target.value)}
                        placeholder="Describe what action you took or your assessment…"
                        style={taStyle} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleAcknowledge(n._id)} disabled={saving}
                        style={btnStyle('#16a34a', saving)}>
                        {saving ? '⏳ Saving…' : '✅ Submit Acknowledgment'}
                      </button>
                      <button onClick={() => { setAckNotifId(null); setAckCategory(''); setAckComment(''); }}
                        style={cancelBtnStyle}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setAckNotifId(n._id); setFormError(''); }}
                    style={btnStyle('#16a34a')}>
                    ✅ Acknowledge This Escalation
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          RAISE NEW ESCALATION FORM
      ══════════════════════════════════════════════════ */}
      <div style={{ background: '#fafbff', border: '1px solid #e0e7ff', borderRadius: 12, padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#3730a3', marginBottom: 14 }}>
          ➕ Raise New Escalation
        </div>

        {/* Issue category */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Issue Category *</label>
          <select value={issueCategory} onChange={e => setIssueCategory(e.target.value)} style={selStyle}>
            <option value="">— Select issue type —</option>
            {ISSUE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Comment */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Details / Clinical Notes *</label>
          <textarea rows="4" value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Describe the issue, patient's condition, what was tried, urgency level…"
            style={taStyle} />
        </div>

        {/* Doctor picker */}
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Escalate to Doctor *</label>
          <select value={selectedDoctor} onChange={e => setSelectedDoctor(e.target.value)} style={selStyle}>
            <option value="">— Select doctor —</option>
            {doctors.map(d => (
              <option key={d.username} value={d.username}>
                {d.name || d.username}
              </option>
            ))}
          </select>
        </div>

        {/* Info box */}
        {selectedDoctor && (
          <div style={{ fontSize: 11, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, padding: '7px 10px', color: '#1d4ed8', marginBottom: 12 }}>
            🔔 <strong>{selectedDoctor}</strong> will get a bell notification + an Urgent task in their calendar (2-day SLA).<br />
            Reminders every <strong>3 hours</strong> until acknowledged. After 48 h, task is marked <strong>Delayed</strong> and reminders switch to every <strong>1 hour</strong>.
          </div>
        )}

        <button onClick={handleEscalate} disabled={saving}
          style={{ padding: '10px 24px', borderRadius: 8, border: 'none',
            cursor: saving ? 'not-allowed' : 'pointer',
            background: saving ? '#9ca3af' : 'linear-gradient(135deg,#ef4444,#b91c1c)',
            color: '#fff', fontWeight: 800, fontSize: 13 }}>
          {saving ? '⏳ Submitting…' : '🚨 Submit Escalation'}
        </button>
      </div>

      {/* ══════════════════════════════════════════════════
          ESCALATION HISTORY
      ══════════════════════════════════════════════════ */}
      {history.length === 0 && !loadError && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13 }}>
          No escalation history yet for this patient.
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1d23', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 4, height: 16, borderRadius: 4, background: '#ef4444', flexShrink: 0 }} />
            History ({history.length})
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[...history].reverse().map((entry, idx) => {
              const statusInfo  = getEntryStatus(entry);
              const liveNotif   = statusInfo?.liveNotif;
              const isAcked     = liveNotif?.acknowledged || !!entry.commentsByDr?.trim();
              const isOverSLA   = statusInfo?.isOverSLA;

              return (
                <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}>
                  {/* Row 1: date + badges */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      {entry.escalationDate
                        ? new Date(entry.escalationDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </span>
                    {entry.issueCategory && (
                      <span style={{ ...badgeStyle, background: '#f3e8ff', color: '#7c3aed', border: '1px solid #ddd6fe' }}>
                        {entry.issueCategory}
                      </span>
                    )}
                    <span style={{ ...badgeStyle, ...statusStyle(isAcked, isOverSLA) }}>
                      {isAcked ? '✅ Acknowledged' : isOverSLA ? '⚠️ Overdue' : '⏳ Pending'}
                    </span>
                    <span style={{ fontSize: 11, color: '#3b82f6' }}>
                      → {entry.escalatedToName || entry.escalatedTo || '—'}
                    </span>
                  </div>

                  {/* Row 2: clinical notes */}
                  {entry.commentsByThe && (
                    <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>
                      <strong>Clinical:</strong> {entry.commentsByThe}
                    </div>
                  )}

                  {/* Row 3: doctor's ack */}
                  {entry.commentsByDr && (
                    <div style={{ fontSize: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, padding: '6px 10px', color: '#166534' }}>
                      <strong>Dr's Response:</strong> {entry.commentsByDr}
                      {liveNotif?.escalationAckCategory && (
                        <span style={{ marginLeft: 8, fontSize: 11, background: '#dcfce7', padding: '1px 6px', borderRadius: 8 }}>
                          {liveNotif.escalationAckCategory}
                        </span>
                      )}
                    </div>
                  )}

                  {/* From live notif ack */}
                  {!entry.commentsByDr && liveNotif?.escalationAckComment && (
                    <div style={{ fontSize: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, padding: '6px 10px', color: '#166534' }}>
                      <strong>Dr's Response:</strong> {liveNotif.escalationAckComment}
                      <span style={{ marginLeft: 8, fontSize: 11, background: '#dcfce7', padding: '1px 6px', borderRadius: 8 }}>
                        {liveNotif.escalationAckCategory}
                      </span>
                    </div>
                  )}

                  {/* Reminder count */}
                  {liveNotif?.reminderCount > 0 && !isAcked && (
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                      🔔 {liveNotif.reminderCount} reminder(s) sent
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      {variant === 'modal' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="modal-btn-cancel" onClick={onClose} style={{ padding: '9px 20px' }}>
            Close
          </button>
        </div>
      )}
    </div>
  );

  if (variant === 'modal') {
    return (
      <div className="modal-overlay" style={{ zIndex: 9999 }}>
        <div className="modal-content wide" style={{ maxWidth: 'min(96vw, 760px)', maxHeight: '92vh', overflowY: 'auto' }}>
          {content}
        </div>
      </div>
    );
  }
  return <div className="escalation-embedded">{content}</div>;
};

/* ── Shared micro-styles ── */
const msgStyle = (bg, border, color) => ({
  background: bg, border: `1px solid ${border}`, color,
  borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13,
});
const btnStyle = (color, disabled) => ({
  padding: '8px 16px', borderRadius: 7, border: 'none',
  background: disabled ? '#9ca3af' : color,
  color: '#fff', fontWeight: 700, fontSize: 12,
  cursor: disabled ? 'not-allowed' : 'pointer',
});
const cancelBtnStyle = {
  padding: '8px 14px', borderRadius: 7,
  border: '1px solid #d1d5db', background: '#fff',
  cursor: 'pointer', fontSize: 12, color: '#374151',
};
const badgeStyle = {
  display: 'inline-block', padding: '2px 8px',
  borderRadius: 10, fontSize: 11, fontWeight: 600,
};
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 3 };
const taStyle    = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 7, resize: 'vertical', fontFamily: 'inherit' };
const selStyle   = { width: '100%', padding: '7px 9px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 7, background: '#fff' };

export default EscalationModal;
